import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
// 电话客服为主营业务，默认展示电话版工作台；如需切换为邮件/在线会话版，改用 <HeroVisualEmail />
import HeroVisualPhone from "./HeroVisualPhone";
// import HeroVisualEmail from "./HeroVisualEmail";

interface BannerBadge {
  value: string;
  label: string;
}

export default function HeroBanner() {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const badges = (t("home.heroBannerV2.badges", { returnObjects: true }) as BannerBadge[]) ?? [];

  const fadeUp = {
    initial: reduceMotion ? false : { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
  };

  return (
    <section className="relative w-full overflow-hidden bg-[#071B33]">
      {/* 背景层 1：主渐变 */}
      <div
        className="absolute inset-0 bg-[radial-gradient(120%_120%_at_15%_0%,#0F3357_0%,#0A2540_45%,#071B33_100%)]"
        aria-hidden
      />
      {/* 背景层 2：右上方品牌色光晕（克制） */}
      <div
        className="absolute -right-[10%] -top-[15%] h-[620px] w-[620px] rounded-full bg-[#00D4AA]/10 blur-[130px]"
        aria-hidden
      />
      {/* 背景层 3：精细网格纹理，向中心淡出 */}
      <div
        className="absolute inset-0 opacity-[0.5] mask-[radial-gradient(80%_60%_at_50%_30%,#000_0%,transparent_75%)]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
        aria-hidden
      />
      {/* 背景层 4：底部柔和过渡，承接下一屏白色板块 */}
      <div
        className="absolute inset-x-0 bottom-0 h-40 bg-linear-to-b from-transparent to-[#071B33]"
        aria-hidden
      />

      <div className="relative z-10 mx-auto max-w-7xl px-4 pb-28 pt-28 sm:px-6 lg:px-8 lg:pt-32">
        <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-10">
          {/* 左侧文案 */}
          <div className="flex flex-col justify-center">
            {/* 眉标：胶囊徽标 */}
            <motion.div
              {...fadeUp}
              className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-[#00D4AA]/25 bg-[#00D4AA]/8 py-1.5 pl-2 pr-3.5 text-[12px] font-medium text-[#8BEAD7] backdrop-blur-sm sm:text-[13px]"
            >
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#00D4AA]/15">
                <span className="h-1.5 w-1.5 rounded-full bg-[#00D4AA]" />
              </span>
              {t("home.heroBannerV2.tag")}
            </motion.div>

            <motion.h1 {...fadeUp} transition={{ delay: 0.08 }} className="max-w-[600px] text-white">
              <span className="block text-[28px] font-bold leading-[1.18] tracking-tight sm:text-[38px] lg:text-[46px]">
                {t("home.heroBannerV2.titleLine1")}
              </span>
              <span className="mt-3 block bg-linear-to-r from-[#00D4AA] to-[#5BE9CF] bg-clip-text text-[17px] font-semibold leading-[1.4] text-transparent sm:text-[20px] lg:text-[22px]">
                {t("home.heroBannerV2.titleLine2")}
              </span>
            </motion.h1>

            <motion.p
              {...fadeUp}
              transition={{ delay: 0.16 }}
              className="mt-5 max-w-[440px] text-[15px] leading-relaxed text-slate-300/85 sm:text-base"
            >
              {t("home.heroBannerV2.slogan")}
            </motion.p>

            <motion.div
              {...fadeUp}
              transition={{ delay: 0.26 }}
              className="mt-8 flex flex-col gap-3.5 sm:flex-row sm:items-center"
            >
              <Link
                to="/schemeCustomization"
                className="group inline-flex items-center justify-center gap-2 rounded-xl bg-[#00D4AA] px-7 py-3.5 text-base font-bold text-[#06243C] shadow-[0_12px_30px_-8px_rgba(0,212,170,0.55)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#16e3ba] hover:shadow-[0_16px_36px_-8px_rgba(0,212,170,0.7)]"
              >
                {t("home.heroBannerV2.ctaPrimary")}
                <ArrowRight
                  className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1"
                  strokeWidth={2.2}
                  aria-hidden
                />
              </Link>
              <Link
                to="/serve"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/3 px-7 py-3.5 text-base font-semibold text-white transition-colors duration-200 hover:border-white/40 hover:bg-white/8"
              >
                {t("home.heroBannerV2.ctaSecondary")}
              </Link>
            </motion.div>

            {/* 信任行 */}
            <motion.div
              {...fadeUp}
              transition={{ delay: 0.34 }}
              className="mt-5 flex items-center gap-2 text-[12px] text-slate-400"
            >
              <ShieldCheck className="h-4 w-4 text-[#00D4AA]" strokeWidth={2} aria-hidden />
              {t("home.partnerWall.title")}
            </motion.div>

            {/* 数据条 */}
            <motion.dl
              {...fadeUp}
              transition={{ delay: 0.42 }}
              className="mt-9 flex w-full max-w-[520px] border-t border-white/10 pt-6"
            >
              {badges.map((b, i) => (
                <div
                  key={i}
                  className={`min-w-0 flex-1 ${
                    i > 0 ? "border-l border-white/12 pl-4 sm:pl-6" : "pr-4 sm:pr-6"
                  }`}
                >
                  <dt className="sr-only">{b.label}</dt>
                  <dd className="bg-linear-to-b from-white to-slate-300 bg-clip-text text-[26px] font-bold leading-none tabular-nums tracking-tight text-transparent sm:text-[30px]">
                    {b.value}
                  </dd>
                  <dd className="mt-2 text-[11px] leading-tight text-slate-400 sm:text-[12px]">
                    {b.label}
                  </dd>
                </div>
              ))}
            </motion.dl>
          </div>

          <div className="overflow-visible">
            <HeroVisualPhone />
          </div>
        </div>
      </div>
    </section>
  );
}
